import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { supabase } from '../../src/lib/supabase';
import { logUsageEvent } from '../../src/lib/logger';
import AnaliseCadastralWizard from './Wizard/AnaliseCadastralWizard.tsx';
import GestaoDetailsModal from './GestaoDetailsModal.tsx';
import { Filter, ChevronDown, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Check, HandHeart, Search, X, Calendar, Clock, Shield, Lock, Download, Loader2, FileText, CheckCircle2, XCircle, RefreshCcw, BarChart3, TrendingUp, DollarSign, Hash, Database, AlertTriangle, User, ExternalLink, ChevronRight as ChevronRightIcon, ArrowLeft, Layers } from 'lucide-react';
import LoadingSpinner from '../../src/components/LoadingSpinner';
import SlaTimer from '../../src/components/SlaTimer';
import { useAuth } from '../../src/contexts/AuthContext';
import { ehGerenteComercial } from '../../src/lib/departamentos';
import ResponsibleFilter from '../../src/components/ResponsibleFilter';
import { Profile, UnidadeConsumidora } from '../../types';
import { exportAnaliseCadastralToExcel } from '../../src/lib/exportAnaliseCadastral';
import { dataDaDecisao, rotuloDecisaoAnalise } from '../../src/lib/exportAnaliseCadastralDados';
import { embedToArray } from '../../src/lib/utils';
import { STATUS_COM_PENDENCIA, STATUS_NA_FILA, comPendenciaNaValidacao, naFilaDaValidacao } from '../../src/lib/validacaoDados';
import { getStatusRateioUc, STATUS_RATEIO_UC_META, type StatusRateioUc } from '../../src/lib/statusRateioUc';

interface ActiveLock {
    negociacao_id: number;
    locked_by: string;
    locked_by_name: string;
    locked_at: string;
}
interface WizardSession {
    user_id: string;
    started_at: string;
    ended_at: string | null;
    duration_seconds: number | null;
}

interface NegociacaoAnalise {
    id: number;
    cliente_nome: string;
    numero_uc: string;
    cpf_cnpj?: string;
    consumo_medio_kwh: number;
    data_assinatura_termo: string;
    status: string;
    analise_cadastral_minutos_realizados?: number;
    responsavel_id?: string;
    unidades_consumidoras?: (Pick<UnidadeConsumidora, 'numero_uc' | 'situacao' | 'cadastro_aprovado' | 'rateio_motivo_travamento' | 'dados_tecnicos_equatorial' | 'rateio_unidade_geradora_id' | 'unidade_geradora'> & { outro_numero_uc?: string, rateio_unidades_geradoras?: { nome: string } | null })[];
    analise_cadastral?: { updated_at: string, created_at: string, decisao_final?: string, decisao_final_at?: string | null, analista_id?: string }[];
    wizard_sessions?: WizardSession[];
    boas_vindas_enviadas?: boolean;
}

/** Formata CPF (11 dígitos) ou CNPJ (14 dígitos) */
const formatCpfCnpj = (value?: string): string => {
    if (!value) return '—';
    const digits = value.replace(/\D/g, '');
    if (digits.length === 11) return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    if (digits.length === 14) return digits.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
    return value;
};

// 'pendente' = fila da analista (termo assinado); 'pendencia' = já analisado e esperando o
// cliente (termo em análise); 'analisado' = já decidido (aprovado, reprovado e pendência).
// Regra em src/lib/validacaoDados.ts.
type FilterMode = 'pendente' | 'pendencia' | 'analisado';

const FILTER_OPTIONS = [
    { id: 'pendente', label: 'Pendentes de Análise' },
    { id: 'pendencia', label: 'Com Pendência' },
    { id: 'analisado', label: 'Analisados' }
];

// Mesmo rótulo da planilha exportada — as duas precisam dizer o que o Kanban diz.
const parseDecisao = rotuloDecisaoAnalise;

type GrupoDecisao = 'aprovado' | 'reprovado' | 'pendencia';

/** Pendência/garantia só é reprovação quando a negociação foi reprovada de fato. */
const ehPendenciaEmAberto = (decisao?: string, status?: string) =>
    (decisao === 'Pendência Documental' || decisao === 'Exigir Garantia') && status !== 'termo_reprovado';

/** Classifica a decisão em um grupo para filtragem.
 *  A decisão registrada tem precedência sobre o status (mesma ordem do parseDecisao). */
const getDecisaoGroup = (decisao?: string, status?: string): GrupoDecisao | null => {
    if (decisao === 'Aprovado' || decisao === 'Aprovado com Ressalvas') return 'aprovado';
    if (ehPendenciaEmAberto(decisao, status)) return 'pendencia';
    if (decisao === 'Reprovado' || decisao === 'Pendência Documental' || decisao === 'Exigir Garantia') return 'reprovado';
    if (status === 'termo_aprovado') return 'aprovado';
    if (status === 'termo_reprovado') return 'reprovado';
    return null;
};

const getDecisaoColor = (decisao?: string, status?: string) => {
    const d = decisao || '';
    if (d === 'Aprovado') return 'text-green-700 bg-green-50 border-green-200 dark:text-green-400 dark:bg-green-900/20 dark:border-green-800/40';
    if (d === 'Aprovado com Ressalvas') return 'text-blue-700 bg-blue-50 border-blue-200 dark:text-blue-400 dark:bg-blue-900/20 dark:border-blue-800/40';
    if (ehPendenciaEmAberto(d, status)) return 'text-amber-700 bg-amber-50 border-amber-200 dark:text-amber-400 dark:bg-amber-900/20 dark:border-amber-800/40';
    if (d === 'Pendência Documental' || d === 'Exigir Garantia' || d === 'Reprovado') return 'text-red-700 bg-red-50 border-red-200 dark:text-red-400 dark:bg-red-900/20 dark:border-red-800/40';
    // Sem decisão registrada — cai no status da negociação
    if (status === 'termo_aprovado') return 'text-green-700 bg-green-50 border-green-200 dark:text-green-400 dark:bg-green-900/20 dark:border-green-800/40';
    if (status === 'termo_reprovado') return 'text-red-700 bg-red-50 border-red-200 dark:text-red-400 dark:bg-red-900/20 dark:border-red-800/40';
    return 'text-gray-600 bg-gray-50 border-gray-200 dark:text-gray-400 dark:bg-gray-800 dark:border-gray-700';
};

/** Formata duração em segundos para formato legível */
const formatDuration = (seconds: number | null | undefined): string => {
    if (seconds == null || seconds <= 0) return '-';
    const mins = Math.round(seconds / 60);
    if (mins < 1) return '<1min';
    if (mins < 60) return `${mins}min`;
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    if (h < 24) return m > 0 ? `${h}h ${m}min` : `${h}h`;
    const d = Math.floor(h / 24);
    const rh = h % 24;
    return rh > 0 ? `${d}d ${rh}h` : `${d}d`;
};

/** Agrupa sessões por user_id e soma durações */
const aggregateSessions = (sessions: WizardSession[] | undefined): { userId: string; totalSeconds: number }[] => {
    if (!sessions || sessions.length === 0) return [];
    const map = new Map<string, number>();
    for (const s of sessions) {
        const secs = s.duration_seconds ?? 0;
        map.set(s.user_id, (map.get(s.user_id) || 0) + secs);
    }
    return Array.from(map.entries())
        .map(([userId, totalSeconds]) => ({ userId, totalSeconds }))
        .sort((a, b) => b.totalSeconds - a.totalSeconds);
};

/** UC como chega no select da listagem (subset de UnidadeConsumidora) */
type UcResumo = NonNullable<NegociacaoAnalise['unidades_consumidoras']>[number];

/** Acima disso a celula deixa de listar UC a UC e vira resumo agregado.
 *  Cliente com 160+ UCs (ex.: prefeituras) esticava a linha por milhares de pixels. */
const LIMITE_UCS_INLINE = 4;

const rotuloUg = (u: UcResumo): string =>
    u.rateio_unidades_geradoras?.nome || (u.unidade_geradora ? `UG ${u.unidade_geradora}` : 'Sem UG');

const CHIP_UG = 'bg-sky-50 dark:bg-sky-900/20 text-sky-700 dark:text-sky-400 border-sky-200/80 dark:border-sky-800/40';
const CHIP_SEM_UG = 'bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 border-slate-200/80 dark:border-slate-700/80';

/** Linha "numero da UC + status de rateio + UG" — usada inline (poucas UCs) e no modal */
const UcLinha: React.FC<{ uc: UcResumo }> = ({ uc }) => {
    const ugNome = uc.rateio_unidades_geradoras?.nome;
    const statusMeta = STATUS_RATEIO_UC_META[getStatusRateioUc(uc)];
    return (
        <div className="flex items-center gap-1.5 flex-wrap">
            <span className="font-mono text-[11px] text-gray-600 dark:text-gray-300 whitespace-nowrap">{uc.numero_uc}</span>
            <span title={statusMeta.label} className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold border whitespace-nowrap ${statusMeta.className}`}>
                {statusMeta.icon} {statusMeta.label}
            </span>
            {ugNome ? (
                <span title={ugNome} className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-bold border whitespace-nowrap truncate max-w-[150px] ${CHIP_UG}`}>
                    {ugNome}
                </span>
            ) : uc.unidade_geradora ? (
                <span title={`UG (legado, sem nome resolvido): ${uc.unidade_geradora}`} className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-bold border whitespace-nowrap ${CHIP_SEM_UG}`}>
                    UG {uc.unidade_geradora}
                </span>
            ) : (
                <span className="text-slate-400 dark:text-slate-500 text-[11px]">Sem UG</span>
            )}
        </div>
    );
};

/**
 * Celula "UG Vinculada": ate LIMITE_UCS_INLINE UCs lista uma a uma (comportamento antigo);
 * acima disso mostra contagem por UG + por status de rateio e joga a lista completa
 * (com busca) num modal, mantendo a altura da linha constante.
 */
const UcsVinculadasCell: React.FC<{ ucs?: UcResumo[]; clienteNome: string }> = ({ ucs, clienteNome }) => {
    const [modalAberto, setModalAberto] = useState(false);
    const [busca, setBusca] = useState('');

    const resumo = useMemo(() => {
        const porUg = new Map<string, number>();
        const porStatus = new Map<StatusRateioUc, number>();
        for (const u of ucs || []) {
            const ug = rotuloUg(u);
            porUg.set(ug, (porUg.get(ug) || 0) + 1);
            const st = getStatusRateioUc(u);
            porStatus.set(st, (porStatus.get(st) || 0) + 1);
        }
        return {
            ugs: Array.from(porUg.entries()).sort((a, b) => b[1] - a[1]),
            status: Array.from(porStatus.entries()).sort((a, b) => b[1] - a[1]),
        };
    }, [ucs]);

    const filtradas = useMemo(() => {
        const q = busca.trim().toLowerCase();
        if (!q) return ucs || [];
        return (ucs || []).filter(u =>
            u.numero_uc?.toLowerCase().includes(q) ||
            u.outro_numero_uc?.toLowerCase().includes(q) ||
            rotuloUg(u).toLowerCase().includes(q) ||
            STATUS_RATEIO_UC_META[getStatusRateioUc(u)].label.toLowerCase().includes(q)
        );
    }, [ucs, busca]);

    useEffect(() => {
        if (!modalAberto) return;
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setModalAberto(false); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [modalAberto]);

    if (!ucs || ucs.length === 0) return null;

    if (ucs.length <= LIMITE_UCS_INLINE) {
        return (
            <div className="inline-flex flex-col items-start gap-1">
                {ucs.map((u, i) => <UcLinha key={i} uc={u} />)}
            </div>
        );
    }

    const ugsVisiveis = resumo.ugs.slice(0, 2);
    const ugsRestantes = resumo.ugs.slice(2);
    const statusVisiveis = resumo.status.slice(0, 3);
    const statusRestantes = resumo.status.slice(3);

    const chipUg = (nome: string, qtd: number, key?: string) => (
        <span
            key={key ?? nome}
            title={`${qtd} ${qtd === 1 ? 'UC' : 'UCs'} — ${nome}`}
            className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold border whitespace-nowrap ${nome === 'Sem UG' ? CHIP_SEM_UG : CHIP_UG}`}
        >
            <span className="truncate max-w-[110px]">{nome}</span>
            <span className="tabular-nums opacity-80">· {qtd}</span>
        </span>
    );

    return (
        <div className="flex flex-col items-start gap-1.5 max-w-[280px]">
            <div className="flex flex-wrap items-center gap-1">
                {ugsVisiveis.map(([nome, qtd]) => chipUg(nome, qtd))}
                {ugsRestantes.length > 0 && (
                    <span
                        title={ugsRestantes.map(([n, q]) => `${n} — ${q}`).join('\n')}
                        className="text-[10px] text-gray-400 dark:text-gray-500 cursor-help underline decoration-dotted whitespace-nowrap"
                    >
                        +{ugsRestantes.length} {ugsRestantes.length === 1 ? 'UG' : 'UGs'}
                    </span>
                )}
            </div>
            <div className="flex flex-wrap items-center gap-1">
                {statusVisiveis.map(([st, qtd]) => {
                    const meta = STATUS_RATEIO_UC_META[st];
                    // com 1 ou 2 status cabe o rotulo; com 3 so o icone + contagem (tooltip explica)
                    return (
                        <span key={st} title={`${qtd} ${qtd === 1 ? 'UC' : 'UCs'} — ${meta.label}`} className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold border whitespace-nowrap ${meta.className}`}>
                            {meta.icon}
                            {statusVisiveis.length <= 2 && <span className="truncate max-w-[110px]">{meta.label}</span>}
                            <span className="tabular-nums opacity-80">{statusVisiveis.length <= 2 ? `· ${qtd}` : qtd}</span>
                        </span>
                    );
                })}
                {statusRestantes.length > 0 && (
                    <span
                        title={statusRestantes.map(([st, q]) => `${STATUS_RATEIO_UC_META[st].label} — ${q}`).join('\n')}
                        className="text-[10px] text-gray-400 dark:text-gray-500 cursor-help underline decoration-dotted whitespace-nowrap"
                    >
                        +{statusRestantes.length}
                    </span>
                )}
            </div>
            <button
                type="button"
                onClick={e => { e.stopPropagation(); setModalAberto(true); }}
                className="inline-flex items-center gap-1 text-[10px] font-bold text-primary hover:underline whitespace-nowrap"
            >
                <Layers size={11} /> Ver as {ucs.length} UCs
            </button>

            {modalAberto && document.body && createPortal(
                <div
                    className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm animate-fade-in p-4"
                    onClick={e => { e.stopPropagation(); setModalAberto(false); }}
                >
                    <div
                        className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-700 w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden"
                        onClick={e => e.stopPropagation()}
                    >
                        <div className="px-5 py-4 border-b border-slate-100 dark:border-slate-800 flex items-start justify-between gap-3">
                            <div className="min-w-0">
                                <h3 className="text-base font-bold text-slate-800 dark:text-slate-200 truncate" title={clienteNome}>{clienteNome}</h3>
                                <p className="text-xs text-slate-500 dark:text-slate-400">{ucs.length} unidades consumidoras vinculadas</p>
                            </div>
                            <button
                                type="button"
                                onClick={() => setModalAberto(false)}
                                title="Fechar"
                                className="p-1.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-all shrink-0"
                            >
                                <X size={18} />
                            </button>
                        </div>

                        <div className="px-5 py-3 bg-slate-50/60 dark:bg-slate-800/30 border-b border-slate-100 dark:border-slate-800 flex flex-wrap gap-1.5">
                            {resumo.ugs.map(([nome, qtd]) => chipUg(nome, qtd, `m-${nome}`))}
                            {resumo.status.map(([st, qtd]) => {
                                const meta = STATUS_RATEIO_UC_META[st];
                                return (
                                    <span key={st} className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold border whitespace-nowrap ${meta.className}`}>
                                        {meta.icon} {meta.label} <span className="tabular-nums opacity-80">· {qtd}</span>
                                    </span>
                                );
                            })}
                        </div>

                        <div className="px-5 py-3 border-b border-slate-100 dark:border-slate-800">
                            <div className="relative">
                                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                                <input
                                    autoFocus
                                    value={busca}
                                    onChange={e => setBusca(e.target.value)}
                                    placeholder="Buscar por UC, UG ou status..."
                                    className="w-full h-9 pl-9 pr-3 text-sm rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-200 outline-none focus:ring-2 focus:ring-primary/40"
                                />
                            </div>
                        </div>

                        <div className="flex-1 overflow-y-auto px-5 py-3">
                            {filtradas.length === 0 ? (
                                <p className="text-sm text-slate-400 dark:text-slate-500 text-center py-8">Nenhuma UC encontrada para "{busca}".</p>
                            ) : (
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5">
                                    {filtradas.map((u, i) => <UcLinha key={i} uc={u} />)}
                                </div>
                            )}
                        </div>

                        {busca.trim() && (
                            <div className="px-5 py-2 border-t border-slate-100 dark:border-slate-800 text-[11px] text-slate-500 dark:text-slate-400">
                                {filtradas.length} de {ucs.length} UCs
                            </div>
                        )}
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

const GestaoCard: React.FC = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [negociacoes, setNegociacoes] = useState<NegociacaoAnalise[]>([]);
    const [allNegociacoes, setAllNegociacoes] = useState<NegociacaoAnalise[]>([]);
    const [loading, setLoading] = useState(true);
    const { profile } = useAuth();
    // #6530: a gerente do Comercial só consulta; analisar, reabrir e dar boas-vindas ficam com o Pós Venda.
    const somenteConsulta = ehGerenteComercial(profile);
    const [selectedNegociacaoId, setSelectedNegociacaoId] = useState<number | null>(null);
    const [selectedDetailsId, setSelectedDetailsId] = useState<number | null>(null);
    const [profiles, setProfiles] = useState<Profile[]>([]);
    const [activeLocks, setActiveLocks] = useState<Map<number, ActiveLock>>(new Map());

    const [filterMode, setFilterMode] = useState<FilterMode>('pendente');
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const filterRef = useRef<HTMLDivElement>(null);

    // Search & column filters
    const [searchText, setSearchText] = useState('');
    const [selectedResponsibles, setSelectedResponsibles] = useState<string[]>([]);
    const [selectedAnalistas, setSelectedAnalistas] = useState<string[]>([]);
    const [filterDecisaoGroup, setFilterDecisaoGroup] = useState<'todos' | GrupoDecisao>('todos');
    const [filterDataDe, setFilterDataDe] = useState('');
    const [filterDataAte, setFilterDataAte] = useState('');
    const [isDecisaoFilterOpen, setIsDecisaoFilterOpen] = useState(false);
    const decisaoFilterRef = useRef<HTMLDivElement>(null);
    const [exporting, setExporting] = useState(false);

    // Dashboard Consumo API Serasa
    const [showDashboardModal, setShowDashboardModal] = useState(false);
    // Analyst drill-down
    const [analystDetail, setAnalystDetail] = useState<{
        nome: string;
        consultas: {
            id: string;
            created_at: string;
            tipo_pessoa: string;
            negociacao_id: number | null;
            cliente_nome: string;
            custo: number;
        }[];
    } | null>(null);
    const [dashboardData, setDashboardData] = useState<{
        totalPF: number; totalPJ: number; totalGeral: number;
        custoEstimado: number;
        porMes: { mes: string; pf: number; pj: number; total: number; custo: number; excedente: boolean }[];
        porUsuario: {
            id: string;
            nome: string;
            pf: number;
            pj: number;
            total: number;
            custo: number;
            consultas: {
                id: string;
                created_at: string;
                tipo_pessoa: string;
                negociacao_id: number | null;
                cliente_nome: string;
                custo: number;
            }[];
        }[];
    } | null>(null);
    const [loadingDashboard, setLoadingDashboard] = useState(false);

    // Pricing tiers (contrato Serasa 17/04/2026)
    // Ate 1.607 consultas/mes: R$ 9,86 PF / R$ 9,95 PJ
    // Acima de 1.607 consultas/mes: R$ 11,30 PF / R$ 11,40 PJ
    const CUSTO_PF_CONTRATUAL = 9.86;
    const CUSTO_PJ_CONTRATUAL = 9.95;
    const CUSTO_PF_EXCEDENTE = 11.30;
    const CUSTO_PJ_EXCEDENTE = 11.40;
    const TETO_MENSAL = 1607;
    // Kept for display convenience (contracted rate)
    const CUSTO_PF = CUSTO_PF_CONTRATUAL;
    const CUSTO_PJ = CUSTO_PJ_CONTRATUAL;

    // Tiered monthly cost: proportional split of ceiling between PF and PJ
    const calcularCustoMes = (pf: number, pj: number): number => {
        const total = pf + pj;
        if (total <= TETO_MENSAL) {
            return pf * CUSTO_PF_CONTRATUAL + pj * CUSTO_PJ_CONTRATUAL;
        }
        const ratio = total > 0 ? pf / total : 0;
        const pfDentro = Math.round(TETO_MENSAL * ratio);
        const pjDentro = TETO_MENSAL - pfDentro;
        return (pfDentro * CUSTO_PF_CONTRATUAL + pjDentro * CUSTO_PJ_CONTRATUAL)
            + ((pf - pfDentro) * CUSTO_PF_EXCEDENTE + (pj - pjDentro) * CUSTO_PJ_EXCEDENTE);
    };

    // Per-consultation cost based on its sequential position within the month
    const custoPorConsulta = (tipo: string, posicaoNoMes: number): number => {
        if (posicaoNoMes <= TETO_MENSAL) {
            return tipo === 'PF' ? CUSTO_PF_CONTRATUAL : CUSTO_PJ_CONTRATUAL;
        }
        return tipo === 'PF' ? CUSTO_PF_EXCEDENTE : CUSTO_PJ_EXCEDENTE;
    };

    const fetchDashboardData = useCallback(async () => {
        setLoadingDashboard(true);
        try {
            // Fetch serasa cache rows with negociacao_id to get client name
            const { data: rows, error } = await supabase
                .from('serasa_consultas_cache')
                .select('id, tipo_pessoa, consultado_por, created_at, negociacao_id')
                .order('created_at', { ascending: false });
            if (error) throw error;
            if (!rows || rows.length === 0) {
                setDashboardData({ totalPF: 0, totalPJ: 0, totalGeral: 0, custoEstimado: 0, porMes: [], porUsuario: [] });
                return;
            }
            const totalPF = rows.filter(r => r.tipo_pessoa === 'PF').length;
            const totalPJ = rows.filter(r => r.tipo_pessoa === 'PJ').length;
            const totalGeral = rows.length;

            // Fetch client names for negociacao_ids
            const negIds = [...new Set(rows.map(r => r.negociacao_id).filter(Boolean))];
            let negMap: Record<number, string> = {};
            if (negIds.length > 0) {
                const { data: negs } = await supabase
                    .from('negociacoes')
                    .select('id, cliente_nome')
                    .in('id', negIds);
                if (negs) negs.forEach(n => { negMap[n.id] = n.cliente_nome; });
            }

            // Group rows by month; sort ascending within each month for position-aware pricing
            const mesMap: Record<string, { pf: number; pj: number; rowsSorted: typeof rows }> = {};
            rows.forEach(r => {
                const d = new Date(r.created_at);
                const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
                if (!mesMap[key]) mesMap[key] = { pf: 0, pj: 0, rowsSorted: [] };
                if (r.tipo_pessoa === 'PF') mesMap[key].pf++; else mesMap[key].pj++;
                mesMap[key].rowsSorted.push(r);
            });
            Object.values(mesMap).forEach(m =>
                m.rowsSorted.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())
            );

            // Build per-consultation cost map using chronological position within the month
            const consultaCustoMap: Record<string, number> = {};
            Object.values(mesMap).forEach(m => {
                m.rowsSorted.forEach((r, idx) => {
                    consultaCustoMap[r.id] = custoPorConsulta(r.tipo_pessoa, idx + 1);
                });
            });

            // porMes uses tiered monthly calculation; flag if month exceeded ceiling
            const porMes = Object.entries(mesMap)
                .sort(([a], [b]) => b.localeCompare(a))
                .map(([mes, v]) => ({
                    mes,
                    pf: v.pf,
                    pj: v.pj,
                    total: v.pf + v.pj,
                    custo: calcularCustoMes(v.pf, v.pj),
                    excedente: (v.pf + v.pj) > TETO_MENSAL,
                }));

            const custoEstimado = porMes.reduce((s, m) => s + m.custo, 0);

            const userIds = [...new Set(rows.map(r => r.consultado_por).filter(Boolean))];
            let userMap: Record<string, string> = {};
            if (userIds.length > 0) {
                const { data: users } = await supabase.from('profiles').select('id, full_name').in('id', userIds);
                if (users) users.forEach(u => { userMap[u.id] = u.full_name; });
            }

            // Build per-user data with position-aware per-consultation cost
            const userAgg: Record<string, {
                pf: number; pj: number;
                consultas: { id: string; created_at: string; tipo_pessoa: string; negociacao_id: number | null; cliente_nome: string; custo: number }[];
            }> = {};
            rows.forEach(r => {
                const uid = r.consultado_por || 'unknown';
                if (!userAgg[uid]) userAgg[uid] = { pf: 0, pj: 0, consultas: [] };
                const custo = consultaCustoMap[r.id] ?? (r.tipo_pessoa === 'PF' ? CUSTO_PF_CONTRATUAL : CUSTO_PJ_CONTRATUAL);
                if (r.tipo_pessoa === 'PF') userAgg[uid].pf++; else userAgg[uid].pj++;
                userAgg[uid].consultas.push({
                    id: r.id,
                    created_at: r.created_at,
                    tipo_pessoa: r.tipo_pessoa,
                    negociacao_id: r.negociacao_id ?? null,
                    cliente_nome: r.negociacao_id ? (negMap[r.negociacao_id] || '—') : '—',
                    custo,
                });
            });

            const porUsuario = Object.entries(userAgg)
                .map(([uid, v]) => ({
                    id: uid,
                    nome: userMap[uid] || 'Desconhecido',
                    pf: v.pf,
                    pj: v.pj,
                    total: v.pf + v.pj,
                    custo: v.consultas.reduce((s, c) => s + c.custo, 0),
                    consultas: v.consultas,
                }))
                .sort((a, b) => b.total - a.total);

            setDashboardData({ totalPF, totalPJ, totalGeral, custoEstimado, porMes, porUsuario });
        } catch (err) {
            console.error('Error fetching dashboard:', err);
        } finally {
            setLoadingDashboard(false);
        }
    }, []);

    const openDashboard = () => { setShowDashboardModal(true); fetchDashboardData(); };

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const ITEMS_PER_PAGE = 10;
    const tableContainerRef = useRef<HTMLDivElement>(null);

    // Close dropdown on outside click
    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (filterRef.current && !filterRef.current.contains(event.target as Node)) {
                setIsFilterOpen(false);
            }
            if (decisaoFilterRef.current && !decisaoFilterRef.current.contains(event.target as Node)) {
                setIsDecisaoFilterOpen(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const fetchActiveLocks = async () => {
        const fifteenMinAgo = new Date(Date.now() - 15 * 60 * 1000).toISOString();
        const { data: locks } = await supabase
            .from('wizard_locks')
            .select('negociacao_id, locked_by, locked_at, last_heartbeat')
            .gte('last_heartbeat', fifteenMinAgo);

        if (!locks || locks.length === 0) {
            setActiveLocks(new Map());
            return;
        }

        const userIds = [...new Set(locks.map(l => l.locked_by))];
        const { data: lockProfiles } = await supabase
            .from('profiles')
            .select('id, full_name')
            .in('id', userIds);

        const profileMap = new Map(lockProfiles?.map(p => [p.id, p.full_name]) || []);
        const lockMap = new Map<number, ActiveLock>();

        for (const lock of locks) {
            lockMap.set(lock.negociacao_id, {
                negociacao_id: lock.negociacao_id,
                locked_by: lock.locked_by,
                locked_by_name: profileMap.get(lock.locked_by) || 'Usuário',
                locked_at: lock.locked_at
            });
        }

        setActiveLocks(lockMap);
    };

    const fetchNegociacoes = async () => {
        setLoading(true);
        try {
            let query = supabase
                .from('negociacoes')
                .select('id, cliente_nome, numero_uc, cpf_cnpj, consumo_medio_kwh, data_assinatura_termo, responsavel_id, status, analise_cadastral_minutos_realizados, boas_vindas_enviadas, unidades_consumidoras(numero_uc, outro_numero_uc, situacao, cadastro_aprovado, rateio_motivo_travamento, dados_tecnicos_equatorial, rateio_unidade_geradora_id, unidade_geradora, rateio_unidades_geradoras(nome)), analise_cadastral(updated_at, created_at, decisao_final, decisao_final_at, analista_id), wizard_sessions(user_id, started_at, ended_at, duration_seconds)');

            if (filterMode === 'pendente') {
                query = query.eq('status', STATUS_NA_FILA);
            } else if (filterMode === 'pendencia') {
                query = query.eq('status', STATUS_COM_PENDENCIA);
            } else {
                query = query.in('status', ['termo_aprovado', 'termo_analise', 'termo_reprovado']);
            }

            const { data, error } = await query;

            if (error) {
                console.error('Erro ao buscar negociações:', error);
                return;
            }

            // embed analise_cadastral vem como objeto (1→1) — o restante do componente lê [0]
            setNegociacoes((data || []).map((n: any) => ({ ...n, analise_cadastral: embedToArray(n.analise_cadastral) })));
        } catch (error) {
            console.error('Erro na requisição:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchProfiles = async () => {
        // Conta de consumidor (Portal) nunca entra em lista de escolha do sistema.
        const { data } = await supabase.from('profiles').select('*').neq('role', 'consumidor');
        if (data) setProfiles(data);
    };

    // Fetch all negotiations for dashboard counts (runs once)
    const fetchAllForDashboard = async () => {
        const { data } = await supabase
            .from('negociacoes')
            .select('id, status, analise_cadastral(decisao_final)')
            .in('status', ['termo_assinado', 'termo_aprovado', 'termo_analise', 'termo_reprovado']);
        setAllNegociacoes((data || []).map((n: any) => ({ ...n, analise_cadastral: embedToArray(n.analise_cadastral) })) as any);
    };

    useEffect(() => {
        fetchProfiles();
        fetchNegociacoes();
        fetchActiveLocks();
        fetchAllForDashboard();
    }, [filterMode]);

    /** Reseta negociação reprovada/pendência para reanálise (volta para 'termo_assinado' no Funil de Vendas) */
    const handlePedirReanalise = async (negId: number, clienteNome: string, responsavelId?: string) => {
        try {
            // Fetch the "Funil de Vendas" pipeline to move the negotiation back to it
            const { data: pipelinesData } = await supabase
                .from('pipelines')
                .select('id, name')
                .eq('name', 'Funil de Vendas')
                .single();

            const updatePayload: Record<string, any> = {
                status: 'termo_assinado',
                stage: 'Termo de adesão',
                updated_at: new Date().toISOString()
            };

            // Move back to Funil de Vendas so it reappears in the CRM Kanban
            if (pipelinesData?.id) {
                updatePayload.pipeline_id = pipelinesData.id;
            }

            const { error } = await supabase
                .from('negociacoes')
                .update(updatePayload)
                .eq('id', negId);
            if (error) throw error;

            const { data: userData } = await supabase.auth.getUser();
            await supabase.from('negociacao_logs').insert({
                negociacao_id: negId,
                acao: 'Pedido de Reanálise:',
                detalhes: 'Negociação reprovada retornou para fila de análise.',
                user_id: userData.user?.id
            });
            logUsageEvent({ userId: userData.user?.id, modulo: 'gestao', funcionalidade: 'dashboard', acao: 'pedir_reanalise', origem: 'acao' });

            if (responsavelId) {
                await supabase.from('notifications').insert({
                    user_id: responsavelId,
                    type: 'analysis_completed',
                    title: '🔁 Reanálise Solicitada',
                    content: `A negociação "${clienteNome}" foi enviada para reanálise e voltou à fila de validação de dados.`,
                    link: '/gestao',
                    read: false
                });
            }

            // Refresh both lists
            fetchNegociacoes();
            fetchAllForDashboard();
        } catch (err) {
            console.error('Erro ao pedir reanálise:', err);
        }
    };

    // Auto-open wizard/details if negociacaoId is in URL
    useEffect(() => {
        const negociacaoId = searchParams.get('negociacaoId');
        if (!negociacaoId || loading) return;
        const id = Number(negociacaoId);
        if (isNaN(id)) return;

        // Check if the negotiation is in the current list
        const found = negociacoes.find(n => n.id === id);
        if (found) {
            if (found.status === 'termo_assinado') {
                setSelectedNegociacaoId(id);
            } else {
                setSelectedDetailsId(id);
            }
        } else {
            // Not in current list — switch to analisado filter and try details
            setSelectedDetailsId(id);
        }

        // Clear param
        const newParams = new URLSearchParams(searchParams);
        newParams.delete('negociacaoId');
        setSearchParams(newParams);
    }, [searchParams, negociacoes, loading]);

    // Realtime subscription for wizard_locks to update lock indicators live
    useEffect(() => {
        const channel = supabase
            .channel('wizard-locks-gestao')
            .on('postgres_changes', { event: '*', schema: 'public', table: 'wizard_locks' }, () => {
                fetchActiveLocks();
            })
            .subscribe();

        return () => {
            supabase.removeChannel(channel);
        };
    }, []);

    // Handle when wizard/modal closes and we need to refresh list
    const handleCloseWizard = () => {
        setSelectedNegociacaoId(null);
        fetchNegociacoes();
        fetchActiveLocks();
    };

    const handleCloseDetails = () => {
        setSelectedDetailsId(null);
        fetchNegociacoes(); // Refresh
    };

    const handleEditJustified = (id: number) => {
        setSelectedDetailsId(null);
        setTimeout(() => {
            setSelectedNegociacaoId(id);
        }, 100);
    };

    const handleWelcome = async (negId: number) => {
        setNegociacoes(prev => prev.map(n => n.id === negId ? { ...n, boas_vindas_enviadas: true } : n));
        await supabase.from('negociacoes').update({ boas_vindas_enviadas: true }).eq('id', negId);
        logUsageEvent({ userId: profile?.id, modulo: 'gestao', funcionalidade: 'dashboard', acao: 'marcar_boas_vindas_enviadas', origem: 'acao' });
    };

    const currentFilterLabel = FILTER_OPTIONS.find(f => f.id === filterMode)?.label;
    /** Lista de quem já passou pela analista: mesmas colunas de decisão e o clique abre o detalhe. */
    const modoAnalisado = filterMode !== 'pendente';

    // Client-side filtering
    const filteredNegociacoes = useMemo(() => {
        let result = negociacoes;

        // Text search (cliente_nome + UC)
        if (searchText.trim()) {
            const q = searchText.toLowerCase().trim();
            result = result.filter(n => {
                const nomeMatch = n.cliente_nome?.toLowerCase().includes(q);
                const ucMatch = n.numero_uc?.toLowerCase().includes(q) || (n as any).outro_numero_uc?.toLowerCase().includes(q);
                const ucsMatch = n.unidades_consumidoras?.some(u => u.numero_uc?.toLowerCase().includes(q) || u.outro_numero_uc?.toLowerCase().includes(q));
                return nomeMatch || ucMatch || ucsMatch;
            });
        }

        // Responsável filter
        if (selectedResponsibles.length > 0) {
            result = result.filter(n => n.responsavel_id && selectedResponsibles.includes(n.responsavel_id));
        }

        // Analista filter (who analyzed)
        if (selectedAnalistas.length > 0) {
            result = result.filter(n => {
                const analistaId = n.analise_cadastral?.[0]?.analista_id;
                return analistaId && selectedAnalistas.includes(analistaId);
            });
        }

        // Decisão group filter (aprovado / reprovado) — o seletor só existe na aba Analisados
        if (filterMode === 'analisado' && filterDecisaoGroup !== 'todos') {
            result = result.filter(n => {
                const decisao = n.analise_cadastral?.[0]?.decisao_final;
                return getDecisaoGroup(decisao, n.status) === filterDecisaoGroup;
            });
        }

        // Date range filter
        if (filterDataDe) {
            result = result.filter(n => n.data_assinatura_termo && n.data_assinatura_termo.split('T')[0] >= filterDataDe);
        }
        if (filterDataAte) {
            result = result.filter(n => n.data_assinatura_termo && n.data_assinatura_termo.split('T')[0] <= filterDataAte);
        }

        // Ordenação por aba — as duas listas têm urgências opostas:
        // • Pendentes: assinatura ASC (mais antigo primeiro = quem espera há mais tempo).
        // • Analisados: data da análise DESC (recém-analisado primeiro). Com ASC aqui,
        //   quem acabava de ser analisado caía na última página e sumia da vista.
        // • Com pendência: data da decisão ASC (a pendência mais antiga primeiro, para cobrar).
        const chaveOrdem = (n: NegociacaoAnalise) =>
            modoAnalisado
                ? (dataDaDecisao(n.analise_cadastral?.[0]) || n.data_assinatura_termo || '')
                : (n.data_assinatura_termo || '');

        // Cópia antes do sort: sem filtro ativo `result` ainda é o array do estado.
        result = [...result].sort((a, b) => (
            filterMode === 'analisado'
                ? chaveOrdem(b).localeCompare(chaveOrdem(a))
                : chaveOrdem(a).localeCompare(chaveOrdem(b))
        ));

        return result;
    }, [negociacoes, filterMode, searchText, selectedResponsibles, selectedAnalistas, filterDecisaoGroup, filterDataDe, filterDataAte]);

    // Reset pagination when filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [searchText, selectedResponsibles, selectedAnalistas, filterDecisaoGroup, filterDataDe, filterDataAte, filterMode]);

    // Pagination logic
    const totalPages = Math.max(1, Math.ceil(filteredNegociacoes.length / ITEMS_PER_PAGE));
    const paginatedNegociacoes = useMemo(() => {
        const start = (currentPage - 1) * ITEMS_PER_PAGE;
        return filteredNegociacoes.slice(start, start + ITEMS_PER_PAGE);
    }, [filteredNegociacoes, currentPage]);

    const goToPage = useCallback((page: number | ((prev: number) => number)) => {
        setCurrentPage(prev => {
            const next = typeof page === 'function' ? page(prev) : page;
            return Math.max(1, Math.min(next, totalPages));
        });
    }, [totalPages]);

    const totalClientes = filteredNegociacoes.length;
    const totalOMSA = filteredNegociacoes.reduce((acc, curr) => acc + (Number(curr.consumo_medio_kwh) || 0), 0);
    const hasActiveFilters = !!(searchText || selectedResponsibles.length > 0 || selectedAnalistas.length > 0 || filterDecisaoGroup !== 'todos' || filterDataDe || filterDataAte);
    const clearAllFilters = () => { setSearchText(''); setSelectedResponsibles([]); setSelectedAnalistas([]); setFilterDecisaoGroup('todos'); setFilterDataDe(''); setFilterDataAte(''); };

    // Dashboard KPI counts
    const dashboardCounts = useMemo(() => {
        let novo = 0, aprovado = 0, reprovado = 0, pendencias = 0;
        for (const n of allNegociacoes) {
            if (naFilaDaValidacao(n.status)) { novo++; continue; }
            // Já analisado, esperando o cliente: fica à parte, fora da fila (era o card "Reanálise").
            if (comPendenciaNaValidacao(n.status)) { pendencias++; continue; }
            const decisao = (n as any).analise_cadastral?.[0]?.decisao_final;
            const grupo = getDecisaoGroup(decisao, n.status);
            if (grupo === 'aprovado') { aprovado++; }
            else if (grupo === 'reprovado') { reprovado++; }
        }
        return { novo, aprovado, reprovado, pendencias };
    }, [allNegociacoes]);

    return (
        <div className="h-full flex flex-col gap-4 pb-4">
            {/* Dashboard KPI Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                {[
                    { key: 'novo', filtro: 'pendente' as FilterMode, dica: 'Na fila da analista. Clique para ver a lista.', label: 'Novo', count: dashboardCounts.novo, icon: FileText, color: 'amber', gradient: 'from-amber-500 to-yellow-500', bg: 'bg-amber-50 dark:bg-amber-900/15', border: 'border-amber-200/80 dark:border-amber-800/40', text: 'text-amber-700 dark:text-amber-400' },
                    { key: 'aprovado', filtro: null, dica: undefined, label: 'Aprovado', count: dashboardCounts.aprovado, icon: CheckCircle2, color: 'green', gradient: 'from-emerald-500 to-green-500', bg: 'bg-emerald-50 dark:bg-emerald-900/15', border: 'border-emerald-200/80 dark:border-emerald-800/40', text: 'text-emerald-700 dark:text-emerald-400' },
                    { key: 'reprovado', filtro: null, dica: undefined, label: 'Reprovado', count: dashboardCounts.reprovado, icon: XCircle, color: 'red', gradient: 'from-red-500 to-rose-500', bg: 'bg-red-50 dark:bg-red-900/15', border: 'border-red-200/80 dark:border-red-800/40', text: 'text-red-700 dark:text-red-400' },
                    { key: 'pendencias', filtro: 'pendencia' as FilterMode, dica: 'Já analisadas, esperando documento, garantia ou confirmação do cliente. Não contam na fila nem no SLA. Clique para ver a lista.', label: 'Pendências', count: dashboardCounts.pendencias, icon: Clock, color: 'orange', gradient: 'from-orange-500 to-amber-600', bg: 'bg-orange-50 dark:bg-orange-900/15', border: 'border-orange-200/80 dark:border-orange-800/40', text: 'text-orange-700 dark:text-orange-400' },
                ].map((card) => {
                    const Icon = card.icon;
                    const filtro = card.filtro;
                    const ativo = !!filtro && filterMode === filtro;
                    return (
                        <div
                            key={card.key}
                            {...(filtro ? {
                                role: 'button',
                                tabIndex: 0,
                                'aria-pressed': ativo,
                                title: card.dica,
                                onClick: () => setFilterMode(filtro),
                                onKeyDown: (e: React.KeyboardEvent) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setFilterMode(filtro); } },
                            } : {})}
                            className={`relative overflow-hidden ${card.bg} border ${card.border} rounded-2xl p-4 sm:p-5 transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5 ${filtro ? 'cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40' : 'cursor-default'} ${ativo ? 'ring-2 ring-offset-1 ring-gray-300 dark:ring-gray-600 dark:ring-offset-gray-900' : ''} group`}
                        >
                            <div className={`absolute -top-4 -right-4 w-20 h-20 rounded-full bg-gradient-to-br ${card.gradient} opacity-[0.07] group-hover:opacity-[0.12] transition-opacity duration-300`}></div>
                            <div className="flex items-center justify-between relative">
                                <div>
                                    <p className="text-[10px] sm:text-xs font-bold uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-1">{card.label}</p>
                                    <p className={`text-2xl sm:text-3xl font-extrabold tabular-nums ${card.text}`}>{card.count}</p>
                                </div>
                                <div className={`p-2.5 sm:p-3 rounded-xl bg-gradient-to-br ${card.gradient} shadow-lg`}>
                                    <Icon size={18} className="text-white" />
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center bg-white dark:bg-gray-800 p-4 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm gap-4">
                <div className="flex flex-wrap items-center gap-4">
                    <h3 className="font-poppins font-bold text-gray-700 dark:text-gray-200 text-lg uppercase tracking-wider">
                        TERMOS DISPONÍVEIS
                    </h3>

                    {/* Dropdown Filter */}
                    <div className="relative group" ref={filterRef}>
                        <button
                            onClick={() => setIsFilterOpen(!isFilterOpen)}
                            className={`flex items-center gap-3 px-4 py-2 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-full shadow-sm hover:border-gray-300 dark:hover:border-gray-500 transition-all min-w-full sm:min-w-[200px] justify-between group flex-shrink-0
                                ${isFilterOpen ? 'ring-2 ring-primary/10 border-primary' : ''}
                            `}
                        >
                            <div className="flex items-center gap-2 text-gray-700 dark:text-gray-200">
                                <Filter size={16} className="text-gray-400 dark:text-gray-500 group-hover:text-primary transition-colors" />
                                <span className="text-sm font-medium">
                                    {currentFilterLabel}
                                </span>
                            </div>
                            <ChevronDown size={14} className={`text-gray-400 transition-transform duration-300 ${isFilterOpen ? 'rotate-180' : ''}`} />
                        </button>

                        {isFilterOpen && (
                            <>
                                <div className="fixed inset-0 z-40" onClick={() => setIsFilterOpen(false)}></div>
                                <div className="absolute top-full left-0 mt-2 w-full min-w-[220px] bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-100 dark:border-gray-700 p-2 z-50 animate-fade-in origin-top-left transform transition-all">
                                    {FILTER_OPTIONS.map((option) => (
                                        <button
                                            key={option.id}
                                            onClick={() => {
                                                setFilterMode(option.id as FilterMode);
                                                setIsFilterOpen(false);
                                            }}
                                            className={`w-full text-left px-3 py-2.5 rounded-xl text-sm transition-all flex items-center justify-between mb-1 last:mb-0
                                                ${filterMode === option.id
                                                    ? 'bg-primary/5 dark:bg-primary/10 text-primary font-bold'
                                                    : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 font-medium'
                                                }
                                            `}
                                        >
                                            {option.label}
                                            {filterMode === option.id && <div className="w-2 h-2 rounded-full bg-primary"></div>}
                                        </button>
                                    ))}
                                </div>
                            </>
                        )}
                    </div>

                    <span className="bg-primary/10 text-primary text-sm font-bold px-3 py-1 rounded-full hidden sm:inline-block">
                        {totalClientes}
                    </span>
                </div>

                <div className="flex items-center gap-3">
                    <div className="text-sm text-gray-500 dark:text-gray-400 font-medium bg-gray-50 dark:bg-gray-700/50 px-4 py-2 rounded-lg whitespace-nowrap">
                        Consumo Total: <span className="text-gray-900 dark:text-gray-100 font-bold">{totalOMSA.toLocaleString('pt-BR')} kWh</span>
                    </div>

                    <button
                        onClick={openDashboard}
                        className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 hover:bg-amber-50 hover:text-amber-700 hover:border-amber-200 dark:hover:bg-amber-900/20 dark:hover:text-amber-400 dark:hover:border-amber-800/50 transition-all shadow-sm hover:-translate-y-[1px]"
                        title="Dashboard de consumo da API Serasa"
                    >
                        <BarChart3 size={13} />
                        Consumo API
                    </button>

                    {modoAnalisado && (
                        <button
                            onClick={async () => {
                                if (exporting || filteredNegociacoes.length === 0) return;
                                setExporting(true);
                                try {
                                    await exportAnaliseCadastralToExcel({
                                        negociacaoIds: filteredNegociacoes.map(n => n.id),
                                        profiles: profiles.map(p => ({ id: p.id, full_name: p.full_name })),
                                    });
                                } catch (err) {
                                    console.error('Export error:', err);
                                } finally {
                                    setExporting(false);
                                }
                            }}
                            disabled={exporting || filteredNegociacoes.length === 0}
                            className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold rounded-lg transition-all shadow-sm shadow-emerald-500/20 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                        >
                            {exporting ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
                            {exporting ? 'Exportando...' : 'Exportar Excel'}
                        </button>
                    )}
                </div>
            </div>

            {/* Search & Filter Bar */}
            <div className="bg-white dark:bg-gray-800 p-3 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm">
                <div className="flex flex-col lg:flex-row gap-3 items-stretch lg:items-center">
                    {/* Text Search */}
                    <div className="relative flex-1 min-w-0">
                        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                        <input
                            type="text"
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            className="w-full h-9 pl-9 pr-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 text-sm font-medium text-gray-900 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all placeholder:text-gray-400"
                            placeholder="Buscar por cliente ou UC..."
                        />
                    </div>

                    {/* Responsável */}
                    <div className="min-w-[160px] flex-shrink-0">
                        <ResponsibleFilter
                            selectedResponsibles={selectedResponsibles}
                            setSelectedResponsibles={setSelectedResponsibles}
                            profiles={profiles}
                            currentUserProfile={profile}
                            viewContext="gestao"
                        />
                    </div>

                    {/* Analista (who analyzed) — only in "analisado" tab */}
                    {modoAnalisado && (
                        <div className="min-w-[160px] flex-shrink-0">
                            <ResponsibleFilter
                                selectedResponsibles={selectedAnalistas}
                                setSelectedResponsibles={setSelectedAnalistas}
                                profiles={profiles}
                                currentUserProfile={profile}
                                viewContext="gestao"
                                label="Analista"
                            />
                        </div>
                    )}

                    {/* Decisão (Aprovado / Reprovado) — only in "analisado" tab */}
                    {filterMode === 'analisado' && (
                        <div className="relative flex-shrink-0" ref={decisaoFilterRef}>
                            <button
                                onClick={() => setIsDecisaoFilterOpen(!isDecisaoFilterOpen)}
                                className={`flex items-center gap-2 px-3 h-9 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-full shadow-sm hover:border-gray-300 dark:hover:border-gray-500 transition-all group
                                    ${isDecisaoFilterOpen ? 'ring-2 ring-primary/10 border-primary' : ''}
                                `}
                            >
                                <Shield size={14} className="text-gray-400 group-hover:text-primary transition-colors" />
                                <span className="text-sm font-medium text-gray-700 dark:text-gray-200 whitespace-nowrap">
                                    {filterDecisaoGroup === 'todos' ? 'Decisão' : filterDecisaoGroup === 'aprovado' ? 'Aprovados' : filterDecisaoGroup === 'pendencia' ? 'Pendências' : 'Reprovados'}
                                </span>
                                <ChevronDown size={13} className={`text-gray-400 transition-transform duration-300 ${isDecisaoFilterOpen ? 'rotate-180' : ''}`} />
                            </button>
                            {isDecisaoFilterOpen && (
                                <>
                                    <div className="fixed inset-0 z-40" onClick={() => setIsDecisaoFilterOpen(false)}></div>
                                    <div className="absolute top-full left-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-100 dark:border-gray-700 p-2 z-50 animate-fade-in">
                                        {[
                                            { id: 'todos', label: 'Todos', color: 'text-gray-600 dark:text-gray-300' },
                                            { id: 'aprovado', label: '✅ Aprovados', color: 'text-green-700 dark:text-green-400' },
                                            { id: 'pendencia', label: '⏳ Pendências', color: 'text-amber-700 dark:text-amber-400' },
                                            { id: 'reprovado', label: '❌ Reprovados', color: 'text-red-700 dark:text-red-400' }
                                        ].map(opt => (
                                            <button
                                                key={opt.id}
                                                onClick={() => {
                                                    setFilterDecisaoGroup(opt.id as any);
                                                    setIsDecisaoFilterOpen(false);
                                                }}
                                                className={`w-full text-left px-3 py-2.5 rounded-xl text-sm transition-all flex items-center justify-between mb-1 last:mb-0
                                                    ${filterDecisaoGroup === opt.id
                                                        ? 'bg-primary/5 dark:bg-primary/10 font-bold'
                                                        : 'hover:bg-gray-50 dark:hover:bg-gray-700 font-medium'
                                                    } ${opt.color}
                                                `}
                                            >
                                                {opt.label}
                                                {filterDecisaoGroup === opt.id && <div className="w-2 h-2 rounded-full bg-primary"></div>}
                                            </button>
                                        ))}
                                    </div>
                                </>
                            )}
                        </div>
                    )}

                    {/* Date range */}
                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 bg-gray-50/50 dark:bg-gray-800/50 p-1.5 rounded-xl border border-gray-200/60 dark:border-gray-700 w-full lg:w-auto">
                        <div className="flex items-center gap-2 flex-1 sm:flex-none">
                            <input
                                type="date"
                                value={filterDataDe}
                                onChange={(e) => setFilterDataDe(e.target.value)}
                                className="text-sm/normal h-9 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-white rounded-lg px-2.5 outline-none focus:ring-2 focus:ring-primary/50 w-full sm:w-auto transition-all"
                                title="Data inicial"
                            />
                            <span className="text-gray-300 text-xs font-bold">→</span>
                            <input
                                type="date"
                                value={filterDataAte}
                                onChange={(e) => setFilterDataAte(e.target.value)}
                                className="text-sm/normal h-9 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-white rounded-lg px-2.5 outline-none focus:ring-2 focus:ring-primary/50 w-full sm:w-auto transition-all"
                                title="Data final"
                            />
                        </div>
                        <button
                            onClick={() => {
                                const n = new Date();
                                setFilterDataDe(new Date(n.getFullYear(), n.getMonth(), 1).toISOString().split('T')[0]);
                                setFilterDataAte(new Date(n.getFullYear(), n.getMonth() + 1, 0).toISOString().split('T')[0]);
                            }}
                            className="flex items-center justify-center gap-1.5 h-9 px-3 text-xs font-bold rounded-lg transition-colors w-full sm:w-auto bg-primary/10 text-primary hover:bg-primary/20"
                        >
                            <Calendar size={13} />Mês Atual
                        </button>
                    </div>

                    {/* Clear filters */}
                    {hasActiveFilters && (
                        <button
                            onClick={clearAllFilters}
                            className="h-9 px-3 flex items-center gap-1.5 rounded-lg text-xs font-bold text-red-600 bg-red-50 hover:bg-red-100 dark:text-red-400 dark:bg-red-900/20 dark:hover:bg-red-900/30 border border-red-200 dark:border-red-800 transition-colors whitespace-nowrap"
                        >
                            <X size={14} />
                            Limpar
                        </button>
                    )}
                </div>
            </div>

            <div className="flex-1 flex flex-col min-h-[400px]" ref={tableContainerRef}>
                {/* Visualização Desktop (Tabela) */}
                <div className="hidden md:flex md:flex-col bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm">
                    <div className="overflow-x-auto" style={{ minHeight: `${ITEMS_PER_PAGE * 73 + 53}px` }}>
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700 text-[11px] uppercase text-gray-500 dark:text-gray-400 font-semibold">
                                    <th className="px-4 py-3">Negociação / Cliente</th>
                                    <th className="px-2 py-3">CPF / CNPJ</th>
                                    <th className="px-2 py-3 text-center">N° de UCs</th>
                                    <th className="px-2 py-3">UG Vinculada</th>
                                    <th className="px-2 py-3 text-right">Média Consumo</th>
                                    <th className="px-3 py-3 text-center">Data Ass.</th>
                                    <th className="px-3 py-3">Vendedor</th>
                                    <th className="px-3 py-3">Sessões de Análise</th>
                                    {modoAnalisado && <th className="px-3 py-3 text-center">Data Análise</th>}
                                    {modoAnalisado && <th className="px-3 py-3">Analisado por</th>}
                                    {modoAnalisado && <th className="px-3 py-3 text-center">Decisão Final</th>}
                                    {modoAnalisado && <th className="px-3 py-3 text-center">Boas-vindas</th>}
                                    <th className="px-3 py-3 text-right">Ações</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 dark:divide-gray-700/50">
                                {loading ? (
                                    <tr>
                                        <td colSpan={modoAnalisado ? 14 : 10} className="px-6 py-12 text-center">
                                            <LoadingSpinner label="Carregando análises..." size="sm" />
                                        </td>
                                    </tr>
                                ) : filteredNegociacoes.length === 0 ? (
                                    <tr>
                                        <td colSpan={modoAnalisado ? 14 : 10} className="px-6 py-12 text-center border-none">
                                            <div className="flex flex-col items-center justify-center text-gray-500 dark:text-gray-400">
                                                <svg className="w-12 h-12 mb-4 text-gray-300 dark:text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                </svg>
                                                <p className="font-medium">Nenhuma análise encontrada com base neste filtro.</p>
                                            </div>
                                        </td>
                                    </tr>
                                ) : (
                                    paginatedNegociacoes.map((neg) => (
                                        <tr
                                            key={neg.id}
                                            className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors group cursor-pointer"
                                            onClick={() => {
                                                if (filterMode === 'pendente') { if (!somenteConsulta) setSelectedNegociacaoId(neg.id); }
                                                else setSelectedDetailsId(neg.id);
                                            }}
                                        >
                                            <td className="px-4 py-3">
                                                <div className="flex flex-col">
                                                    <div className="flex items-center gap-2">
                                                        <span className="font-semibold text-gray-900 dark:text-gray-100 text-[13px]">{neg.cliente_nome}</span>
                                                        {activeLocks.has(neg.id) && (() => {
                                                            const lock = activeLocks.get(neg.id)!;
                                                            const isMe = lock.locked_by === profile?.id;
                                                            const sinceTime = new Date(lock.locked_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
                                                            return (
                                                                <span
                                                                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border whitespace-nowrap animate-fade-in ${isMe
                                                                            ? 'text-primary bg-primary/10 border-primary/20 dark:bg-primary/15 dark:border-primary/30'
                                                                            : 'text-amber-700 bg-amber-50 border-amber-200 dark:text-amber-400 dark:bg-amber-900/20 dark:border-amber-800/40'
                                                                        }`}
                                                                    title={`${isMe ? 'Você está' : lock.locked_by_name + ' está'} analisando desde ${sinceTime}`}
                                                                >
                                                                    <Lock size={10} className={isMe ? 'text-primary' : 'text-amber-500'} />
                                                                    {isMe ? 'Você' : lock.locked_by_name}
                                                                </span>
                                                            );
                                                        })()}
                                                    </div>
                                                    <SlaTimer tipo="analise_cadastral" status={neg.status} startedAt={neg.data_assinatura_termo} minutosRealizados={neg.analise_cadastral_minutos_realizados} />
                                                </div>
                                            </td>
                                            <td className="px-2 py-3">
                                                <span className="text-[11px] text-gray-600 dark:text-gray-300 font-mono bg-gray-50 dark:bg-gray-700/50 px-1.5 py-1 rounded inline-block tracking-tight">
                                                    {formatCpfCnpj(neg.cpf_cnpj)}
                                                </span>
                                            </td>
                                            <td className="px-2 py-3 text-center">
                                                {(() => {
                                                    const count = neg.unidades_consumidoras?.length || (neg.numero_uc ? 1 : 0);
                                                    return (
                                                        <span className="inline-flex items-center justify-center min-w-[36px] px-2.5 py-1 rounded-full text-xs font-bold bg-primary/10 text-primary border border-primary/20">
                                                            {count} {count === 1 ? 'UC' : 'UCs'}
                                                        </span>
                                                    );
                                                })()}
                                            </td>
                                            <td className="px-2 py-3 align-top">
                                                <UcsVinculadasCell ucs={neg.unidades_consumidoras} clienteNome={neg.cliente_nome} />
                                            </td>
                                            <td className="px-2 py-3 text-right">
                                                <span className="text-xs font-semibold text-gray-700 dark:text-gray-200 tabular-nums whitespace-nowrap">
                                                    {Number(neg.consumo_medio_kwh) > 0 ? `${Number(neg.consumo_medio_kwh).toLocaleString('pt-BR')} kWh` : '—'}
                                                </span>
                                            </td>
                                            <td className="px-3 py-3 text-center">
                                                {neg.data_assinatura_termo ? (
                                                    <span className="inline-flex items-center text-xs text-gray-600 dark:text-gray-300 font-medium whitespace-nowrap">
                                                        <svg className="w-3.5 h-3.5 mr-1 text-gray-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                        </svg>
                                                        {neg.data_assinatura_termo.split('T')[0].split('-').reverse().join('/')}
                                                    </span>
                                                ) : (
                                                    <span className="text-gray-400 dark:text-gray-500 text-xs">-</span>
                                                )}
                                            </td>
                                            <td className="px-3 py-3 text-xs text-gray-600 dark:text-gray-300 max-w-[120px] truncate" title={profiles.find(p => p.id === neg.responsavel_id)?.full_name || 'Sem responsável'}>
                                                {profiles.find(p => p.id === neg.responsavel_id)?.full_name || 'Sem responsável'}
                                            </td>
                                            <td className="px-3 py-3">
                                                {(() => {
                                                    const agg = aggregateSessions(neg.wizard_sessions);
                                                    if (agg.length === 0) return <span className="text-gray-400 dark:text-gray-500 text-xs italic">Sem sessões</span>;
                                                    const totalSecs = agg.reduce((s, a) => s + a.totalSeconds, 0);
                                                    return (
                                                        <div className="flex flex-col gap-1">
                                                            {agg.slice(0, 3).map((a) => {
                                                                const fullName = profiles.find(p => p.id === a.userId)?.full_name || '?';
                                                                return (
                                                                    <div key={a.userId} className="flex items-center gap-1.5 text-xs">
                                                                        <div className="w-5 h-5 rounded-full bg-primary/10 dark:bg-primary/20 flex items-center justify-center text-[9px] font-bold text-primary shrink-0">
                                                                            {fullName.charAt(0)}
                                                                        </div>
                                                                        <span className="text-gray-700 dark:text-gray-300 font-medium truncate max-w-[100px]">{fullName}</span>
                                                                        <span className="text-gray-400 dark:text-gray-500">—</span>
                                                                        <span className="font-semibold text-gray-800 dark:text-gray-200 whitespace-nowrap">{formatDuration(a.totalSeconds)}</span>
                                                                    </div>
                                                                );
                                                            })}
                                                            {agg.length > 3 && (() => {
                                                                const hidden = agg.slice(3).map(a => {
                                                                    const name = profiles.find(p => p.id === a.userId)?.full_name || '?';
                                                                    return `${name} — ${formatDuration(a.totalSeconds)}`;
                                                                });
                                                                return <span className="text-[10px] text-gray-400 cursor-help underline decoration-dotted" title={hidden.join('\n')}>+{agg.length - 3} mais</span>;
                                                            })()}
                                                            <div className="border-t border-gray-100 dark:border-gray-700 pt-1 mt-0.5 flex items-center gap-1 text-[10px]">
                                                                <Clock size={10} className="text-primary" />
                                                                <span className="font-bold text-primary">{formatDuration(totalSecs)}</span>
                                                            </div>
                                                        </div>
                                                    );
                                                })()}
                                            </td>
                                            {modoAnalisado && (
                                                <td className="px-3 py-3 text-center">
                                                    {neg.analise_cadastral && neg.analise_cadastral.length > 0 ? (
                                                        <span className="inline-flex items-center text-xs text-gray-600 dark:text-gray-300 font-medium whitespace-nowrap">
                                                            <svg className="w-3.5 h-3.5 mr-1 text-gray-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                            </svg>
                                                            {new Date(dataDaDecisao(neg.analise_cadastral[0])!).toLocaleDateString('pt-BR')}
                                                        </span>
                                                    ) : (
                                                        <span className="text-gray-400 dark:text-gray-500 text-xs">-</span>
                                                    )}
                                                </td>
                                            )}
                                            {modoAnalisado && (
                                                <td className="px-3 py-3 text-xs text-gray-600 dark:text-gray-300 max-w-[110px] truncate">
                                                    {(() => {
                                                        const analista = neg.analise_cadastral?.[0]?.analista_id;
                                                        return analista ? profiles.find(p => p.id === analista)?.full_name || '-' : '-';
                                                    })()}
                                                </td>
                                            )}
                                            {modoAnalisado && (
                                                <td className="px-3 py-3 text-center">
                                                    {(() => {
                                                        const decisao = neg.analise_cadastral?.[0]?.decisao_final;
                                                        return (
                                                            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold border ${getDecisaoColor(decisao, neg.status)}`}>
                                                                {parseDecisao(decisao, neg.status)}
                                                            </span>
                                                        );
                                                    })()}
                                                </td>
                                            )}
                                            {modoAnalisado && (
                                                <td className="px-3 py-3 text-center">
                                                    {neg.boas_vindas_enviadas ? (
                                                        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold text-green-700 bg-green-50 dark:text-green-400 dark:bg-green-900/20 select-none">
                                                            <Check size={14} />
                                                            Enviadas
                                                        </span>
                                                    ) : somenteConsulta ? (
                                                        <span className="text-xs text-gray-500 dark:text-gray-400">Não enviadas</span>
                                                    ) : (
                                                        <button
                                                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 hover:bg-amber-100 dark:text-amber-400 dark:bg-amber-900/20 dark:border-amber-700 dark:hover:bg-amber-900/40 transition-all cursor-pointer whitespace-nowrap"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleWelcome(neg.id);
                                                            }}
                                                        >
                                                            <HandHeart size={14} />
                                                            Dar boas-vindas
                                                        </button>
                                                    )}
                                                </td>
                                            )}
                                            <td className="px-3 py-3 text-right">
                                                <div className="flex items-center justify-end gap-2">
                                                    {filterMode === 'pendente' ? (
                                                        somenteConsulta ? (
                                                            <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">Aguardando análise</span>
                                                        ) : (
                                                        <button
                                                            className="text-xs font-semibold text-primary hover:text-white border border-primary hover:bg-primary px-3 py-1.5 rounded-lg transition-all focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1 dark:focus:ring-offset-gray-800 whitespace-nowrap"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setSelectedNegociacaoId(neg.id);
                                                            }}
                                                        >
                                                            Realizar Análise
                                                        </button>
                                                        )
                                                    ) : (
                                                        <>
                                                            <button
                                                                className="text-xs font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 border border-gray-200 dark:text-gray-300 dark:bg-gray-700 dark:border-gray-600 dark:hover:bg-gray-600 px-4 py-2 rounded-lg transition-all whitespace-nowrap"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setSelectedDetailsId(neg.id);
                                                                }}
                                                            >
                                                                Ver Análise
                                                            </button>
                                                            {neg.status === 'termo_reprovado' && !somenteConsulta && (
                                                                <button
                                                                    className="inline-flex items-center gap-1.5 text-xs font-bold text-blue-700 bg-blue-50 hover:bg-blue-100 border border-blue-200 dark:text-blue-400 dark:bg-blue-900/20 dark:border-blue-800/50 dark:hover:bg-blue-900/40 px-3 py-2 rounded-lg transition-all whitespace-nowrap"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handlePedirReanalise(neg.id, neg.cliente_nome, neg.responsavel_id);
                                                                    }}
                                                                >
                                                                    <RefreshCcw size={13} />
                                                                    Reanálise
                                                                </button>
                                                            )}
                                                        </>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination Controls */}
                    {totalPages > 1 && (
                        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-6 py-3 border-t border-gray-100 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50 rounded-b-xl">
                            <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">
                                Mostrando {((currentPage - 1) * ITEMS_PER_PAGE) + 1}–{Math.min(currentPage * ITEMS_PER_PAGE, totalClientes)} de {totalClientes}
                            </span>
                            <div className="flex items-center gap-1">
                                <button
                                    onClick={() => goToPage(1)}
                                    disabled={currentPage === 1}
                                    className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                                    title="Primeira página"
                                >
                                    <ChevronsLeft size={16} />
                                </button>
                                <button
                                    onClick={() => goToPage(p => Math.max(1, p - 1))}
                                    disabled={currentPage === 1}
                                    className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                                    title="Anterior"
                                >
                                    <ChevronLeft size={16} />
                                </button>

                                {(() => {
                                    const pages: (number | '...')[] = [];
                                    if (totalPages <= 7) {
                                        for (let i = 1; i <= totalPages; i++) pages.push(i);
                                    } else {
                                        pages.push(1);
                                        if (currentPage > 3) pages.push('...');
                                        const start = Math.max(2, currentPage - 1);
                                        const end = Math.min(totalPages - 1, currentPage + 1);
                                        for (let i = start; i <= end; i++) pages.push(i);
                                        if (currentPage < totalPages - 2) pages.push('...');
                                        pages.push(totalPages);
                                    }
                                    return pages.map((p, idx) =>
                                        p === '...' ? (
                                            <span key={`dots-${idx}`} className="px-1 text-gray-400 text-xs select-none">…</span>
                                        ) : (
                                            <button
                                                key={p}
                                                onClick={() => goToPage(p as number)}
                                                className={`min-w-[32px] h-8 rounded-lg text-xs font-bold transition-all ${currentPage === p
                                                        ? 'bg-primary text-white shadow-sm'
                                                        : 'text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                                                    }`}
                                            >
                                                {p}
                                            </button>
                                        )
                                    );
                                })()}

                                <button
                                    onClick={() => goToPage(p => Math.min(totalPages, p + 1))}
                                    disabled={currentPage === totalPages}
                                    className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                                    title="Próxima"
                                >
                                    <ChevronRight size={16} />
                                </button>
                                <button
                                    onClick={() => goToPage(totalPages)}
                                    disabled={currentPage === totalPages}
                                    className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                                    title="Última página"
                                >
                                    <ChevronsRight size={16} />
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* Visualização Mobile (Cards) */}
                <div className="md:hidden flex flex-col gap-4">
                    {loading ? (
                        <div className="bg-white dark:bg-gray-800 rounded-xl p-8 border border-gray-100 dark:border-gray-700 shadow-sm">
                            <LoadingSpinner label="Carregando análises..." size="sm" />
                        </div>
                    ) : filteredNegociacoes.length === 0 ? (
                        <div className="bg-white dark:bg-gray-800 rounded-xl p-8 border border-gray-100 dark:border-gray-700 flex flex-col items-center justify-center text-gray-500 dark:text-gray-400 shadow-sm">
                            <svg className="w-12 h-12 mb-4 text-gray-300 dark:text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            <p className="font-medium text-center">Nenhuma análise encontrada.</p>
                        </div>
                    ) : (
                        paginatedNegociacoes.map((neg) => (
                            <div
                                key={neg.id}
                                className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-4 overflow-hidden"
                            >
                                {/* Cabeçalho do Card */}
                                <div className="border-b border-gray-100 dark:border-gray-700 pb-3 mb-3">
                                    <div className="flex justify-between items-start mb-2">
                                        <h4 className="font-bold text-gray-900 dark:text-gray-100 text-base">{neg.cliente_nome}</h4>
                                        <SlaTimer tipo="analise_cadastral" status={neg.status} startedAt={neg.data_assinatura_termo} minutosRealizados={neg.analise_cadastral_minutos_realizados} />
                                    </div>
                                    {activeLocks.has(neg.id) && (() => {
                                        const lock = activeLocks.get(neg.id)!;
                                        const isMe = lock.locked_by === profile?.id;
                                        const sinceTime = new Date(lock.locked_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
                                        return (
                                            <div className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[11px] font-bold mb-2 animate-fade-in ${isMe
                                                    ? 'text-primary bg-primary/10 border border-primary/20'
                                                    : 'text-amber-700 bg-amber-50 border border-amber-200 dark:text-amber-400 dark:bg-amber-900/20 dark:border-amber-800/40'
                                                }`}>
                                                <Lock size={12} />
                                                <span>{isMe ? 'Você está' : `${lock.locked_by_name} está`} analisando</span>
                                                <span className="text-[10px] opacity-70 ml-auto">desde {sinceTime}</span>
                                            </div>
                                        );
                                    })()}
                                    <div className="flex flex-wrap items-center justify-between gap-2">
                                        <div className="flex flex-col items-start gap-1">
                                            {(() => {
                                                const count = neg.unidades_consumidoras?.length || (neg.numero_uc ? 1 : 0);
                                                return (
                                                    <span className="text-xs text-gray-600 dark:text-gray-300 font-mono bg-gray-50 dark:bg-gray-700/50 px-2 py-1 rounded">
                                                        {count} {count === 1 ? 'UC' : 'UCs'}
                                                    </span>
                                                );
                                            })()}
                                            <UcsVinculadasCell ucs={neg.unidades_consumidoras} clienteNome={neg.cliente_nome} />
                                        </div>
                                        {modoAnalisado && (() => {
                                            const decisao = neg.analise_cadastral?.[0]?.decisao_final;
                                            return (
                                                <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide border ${getDecisaoColor(decisao, neg.status)}`}>
                                                    {parseDecisao(decisao, neg.status)}
                                                </span>
                                            );
                                        })()}
                                    </div>
                                </div>

                                {/* Corpo do Card */}
                                <div className="grid grid-cols-2 gap-y-3 gap-x-2 text-sm mb-3">
                                    <div>
                                        <div className="text-gray-500 dark:text-gray-400 text-xs mb-0.5">CPF / CNPJ</div>
                                        <div className="text-gray-800 dark:text-gray-200 font-medium font-mono text-xs">
                                            {formatCpfCnpj(neg.cpf_cnpj)}
                                        </div>
                                    </div>
                                    <div>
                                        <div className="text-gray-500 dark:text-gray-400 text-xs mb-0.5">Média Consumo</div>
                                        <div className="text-gray-800 dark:text-gray-200 font-medium">
                                            {Number(neg.consumo_medio_kwh) > 0 ? `${Number(neg.consumo_medio_kwh).toLocaleString('pt-BR')} kWh` : '—'}
                                        </div>
                                    </div>
                                    <div>
                                        <div className="text-gray-500 dark:text-gray-400 text-xs mb-0.5">Assinatura</div>
                                        <div className="text-gray-800 dark:text-gray-200 font-medium">
                                            {neg.data_assinatura_termo ? neg.data_assinatura_termo.split('T')[0].split('-').reverse().join('/') : '-'}
                                        </div>
                                    </div>
                                    <div>
                                        <div className="text-gray-500 dark:text-gray-400 text-xs mb-0.5">Vendedor</div>
                                        <div className="text-gray-800 dark:text-gray-200 font-medium truncate">
                                            {profiles.find(p => p.id === neg.responsavel_id)?.full_name || '-'}
                                        </div>
                                    </div>
                                    {modoAnalisado && (
                                        <div>
                                            <div className="text-gray-500 dark:text-gray-400 text-xs mb-0.5">Data Análise</div>
                                            <div className="text-gray-800 dark:text-gray-200 font-medium">
                                                {neg.analise_cadastral && neg.analise_cadastral.length > 0
                                                    ? new Date(dataDaDecisao(neg.analise_cadastral[0])!).toLocaleDateString('pt-BR')
                                                    : '-'}
                                            </div>
                                        </div>
                                    )}
                                    {modoAnalisado && (
                                        <div>
                                            <div className="text-gray-500 dark:text-gray-400 text-xs mb-0.5">Analisado por</div>
                                            <div className="text-gray-800 dark:text-gray-200 font-medium truncate">
                                                {(() => {
                                                    const analista = neg.analise_cadastral?.[0]?.analista_id;
                                                    return analista ? profiles.find(p => p.id === analista)?.full_name || '-' : '-';
                                                })()}
                                            </div>
                                        </div>
                                    )}
                                    {/* Sessões de Análise - mostra sempre */}
                                    <div className="col-span-2">
                                        <div className="text-gray-500 dark:text-gray-400 text-xs mb-1">Sessões de Análise</div>
                                        {(() => {
                                            const agg = aggregateSessions(neg.wizard_sessions);
                                            if (agg.length === 0) return <span className="text-gray-400 dark:text-gray-500 text-xs italic">Sem sessões</span>;
                                            const totalSecs = agg.reduce((s, a) => s + a.totalSeconds, 0);
                                            return (
                                                <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                                                    {agg.map((a) => {
                                                        const fullName = profiles.find(p => p.id === a.userId)?.full_name || '?';
                                                        return (
                                                            <span key={a.userId} className="inline-flex items-center gap-1 text-xs">
                                                                <span className="font-medium text-gray-700 dark:text-gray-300">{fullName}</span>
                                                                <span className="font-bold text-gray-800 dark:text-gray-200">{formatDuration(a.totalSeconds)}</span>
                                                            </span>
                                                        );
                                                    })}
                                                    <span className="text-[10px] font-bold text-primary flex items-center gap-0.5">
                                                        <Clock size={10} />{formatDuration(totalSecs)}
                                                    </span>
                                                </div>
                                            );
                                        })()}
                                    </div>
                                </div>

                                {/* Boas-vindas (mobile) */}
                                {modoAnalisado && (
                                    <div className="pb-2">
                                        {neg.boas_vindas_enviadas ? (
                                            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-semibold text-green-700 bg-green-50 dark:text-green-400 dark:bg-green-900/20 select-none">
                                                <Check size={13} />
                                                Boas-vindas enviadas
                                            </span>
                                        ) : (
                                            <button
                                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-bold text-amber-700 bg-amber-50 border border-amber-200 hover:bg-amber-100 dark:text-amber-400 dark:bg-amber-900/20 dark:border-amber-700 dark:hover:bg-amber-900/40 transition-colors"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleWelcome(neg.id);
                                                }}
                                            >
                                                <HandHeart size={13} />
                                                Dar boas-vindas
                                            </button>
                                        )}
                                    </div>
                                )}

                                {/* Rodapé / Ações */}
                                <div className="pt-3 border-t border-gray-100 dark:border-gray-700 flex justify-end gap-2">
                                    {filterMode === 'pendente' ? (
                                        <button
                                            className="text-[11px] font-bold text-primary border border-primary/30 bg-primary/5 hover:bg-primary hover:text-white px-3 py-1.5 rounded-lg transition-colors flex items-center justify-center"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setSelectedNegociacaoId(neg.id);
                                            }}
                                        >
                                            REALIZAR ANÁLISE
                                        </button>
                                    ) : (
                                        <>
                                            <button
                                                className="text-[11px] font-bold text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-200 dark:hover:bg-gray-600 px-3 py-1.5 rounded-lg transition-colors flex items-center justify-center"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setSelectedDetailsId(neg.id);
                                                }}
                                            >
                                                VER ANÁLISE
                                            </button>
                                            {neg.status === 'termo_reprovado' && (
                                                <button
                                                    className="inline-flex items-center gap-1 text-[11px] font-bold text-blue-700 bg-blue-50 hover:bg-blue-100 border border-blue-200 dark:text-blue-400 dark:bg-blue-900/20 dark:border-blue-800/50 dark:hover:bg-blue-900/40 px-3 py-1.5 rounded-lg transition-colors"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handlePedirReanalise(neg.id, neg.cliente_nome, neg.responsavel_id);
                                                    }}
                                                >
                                                    <RefreshCcw size={12} />
                                                    REANÁLISE
                                                </button>
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>
                        ))
                    )}

                    {/* Mobile Pagination */}
                    {totalPages > 1 && (
                        <div className="flex items-center justify-between gap-3 mt-4 bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm px-4 py-3">
                            <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">
                                {((currentPage - 1) * ITEMS_PER_PAGE) + 1}–{Math.min(currentPage * ITEMS_PER_PAGE, totalClientes)} de {totalClientes}
                            </span>
                            <div className="flex items-center gap-1">
                                <button
                                    onClick={() => goToPage(p => Math.max(1, p - 1))}
                                    disabled={currentPage === 1}
                                    className="p-2 rounded-lg text-gray-500 hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                                >
                                    <ChevronLeft size={18} />
                                </button>
                                <span className="text-xs font-bold text-gray-700 dark:text-gray-300 min-w-[50px] text-center">
                                    {currentPage} / {totalPages}
                                </span>
                                <button
                                    onClick={() => goToPage(p => Math.min(totalPages, p + 1))}
                                    disabled={currentPage === totalPages}
                                    className="p-2 rounded-lg text-gray-500 hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                                >
                                    <ChevronRight size={18} />
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Modal for Wizard */}
            {selectedNegociacaoId && document.body && createPortal(
                <div
                    className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm animate-fade-in p-4"
                >
                    <div
                        className="bg-gray-100 dark:bg-slate-900 rounded-2xl w-full max-w-[95vw] h-[90vh] shadow-2xl overflow-hidden flex flex-col relative"
                        onClick={e => e.stopPropagation()}
                    >
                        <AnaliseCadastralWizard
                            negociacaoId={selectedNegociacaoId}
                            onClose={handleCloseWizard}
                        />
                    </div>
                </div>,
                document.body
            )}

            {/* Modal for Read-Only Details */}
            {selectedDetailsId && document.body && createPortal(
                <GestaoDetailsModal
                    negociacaoId={selectedDetailsId}
                    onClose={handleCloseDetails}
                    onReopenEdit={handleEditJustified}
                    hideReviewButton={somenteConsulta}
                />,
                document.body
            )}

            {/* Dashboard Consumo API Modal */}
            {showDashboardModal && createPortal(
                <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }} onClick={() => { setShowDashboardModal(false); setAnalystDetail(null); }}>
                    <div
                        className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-700 w-full max-w-2xl overflow-hidden max-h-[90vh] flex flex-col"
                        style={{ animation: 'modal-enter 0.2s ease-out' }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="px-6 py-4 bg-gradient-to-r from-slate-50 to-amber-50/50 dark:from-slate-800/60 dark:to-amber-900/10 border-b border-slate-100 dark:border-slate-800">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    {analystDetail && (
                                        <button onClick={() => setAnalystDetail(null)} className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-all">
                                            <ArrowLeft size={16} />
                                        </button>
                                    )}
                                    <div className="p-2.5 bg-gradient-to-br from-amber-100 to-orange-100 dark:from-amber-800/30 dark:to-orange-800/20 rounded-xl shadow-sm">
                                        {analystDetail ? <User size={18} className="text-primary" /> : <BarChart3 size={18} className="text-amber-600 dark:text-amber-400" />}
                                    </div>
                                    <div>
                                        <h3 className="text-base font-bold text-slate-800 dark:text-slate-200">
                                            {analystDetail ? analystDetail.nome : 'Dashboard de Consumo API'}
                                        </h3>
                                        <p className="text-[10px] font-extrabold text-amber-600/80 dark:text-amber-400/70 uppercase tracking-[0.12em]">
                                            {analystDetail ? `${analystDetail.consultas.length} consulta${analystDetail.consultas.length !== 1 ? 's' : ''} • Serasa Experian` : 'Serasa Experian • Relatório Básico'}
                                        </p>
                                    </div>
                                </div>
                                <button onClick={() => { setShowDashboardModal(false); setAnalystDetail(null); }} className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-all">
                                    <X size={18} />
                                </button>
                            </div>
                        </div>

                        <div className="px-6 py-5 space-y-5 overflow-y-auto flex-1">
                            {loadingDashboard ? (
                                <div className="flex flex-col items-center justify-center py-12 gap-3">
                                    <Loader2 size={28} className="animate-spin text-amber-500" />
                                    <p className="text-sm text-slate-400 font-medium">Carregando dados...</p>
                                </div>
                            ) : analystDetail ? (
                                (() => {
                                    const mesesNomes = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
                                    const monthlyMap: Record<string, { pf: number; pj: number; custo: number; consultas: typeof analystDetail.consultas }> = {};
                                    analystDetail.consultas.forEach(c => {
                                        const d = new Date(c.created_at);
                                        const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
                                        if (!monthlyMap[key]) monthlyMap[key] = { pf: 0, pj: 0, custo: 0, consultas: [] };
                                        if (c.tipo_pessoa === 'PF') monthlyMap[key].pf++; else monthlyMap[key].pj++;
                                        monthlyMap[key].custo += c.custo;
                                        monthlyMap[key].consultas.push(c);
                                    });
                                    const sortedMonths = Object.entries(monthlyMap).sort(([a], [b]) => b.localeCompare(a));
                                    const totalCusto = analystDetail.consultas.reduce((s, c) => s + c.custo, 0);
                                    const totalPF = analystDetail.consultas.filter(c => c.tipo_pessoa === 'PF').length;
                                    const totalPJ = analystDetail.consultas.filter(c => c.tipo_pessoa === 'PJ').length;
                                    return (
                                        <>
                                            <div className="grid grid-cols-3 gap-3">
                                                <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-4 border border-blue-100 dark:border-blue-800/40">
                                                    <p className="text-[9px] font-extrabold text-blue-500/80 uppercase tracking-widest mb-1">Total PF</p>
                                                    <p className="text-2xl font-black text-blue-700 dark:text-blue-300 tabular-nums">{totalPF}</p>
                                                    <p className="text-[10px] text-blue-500 font-medium mt-0.5">{(totalPF * CUSTO_PF).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</p>
                                                </div>
                                                <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded-xl p-4 border border-emerald-100 dark:border-emerald-800/40">
                                                    <p className="text-[9px] font-extrabold text-emerald-500/80 uppercase tracking-widest mb-1">Total PJ</p>
                                                    <p className="text-2xl font-black text-emerald-700 dark:text-emerald-300 tabular-nums">{totalPJ}</p>
                                                    <p className="text-[10px] text-emerald-500 font-medium mt-0.5">{(totalPJ * CUSTO_PJ).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</p>
                                                </div>
                                                <div className="bg-amber-50 dark:bg-amber-900/20 rounded-xl p-4 border border-amber-200 dark:border-amber-800/40">
                                                    <p className="text-[9px] font-extrabold text-amber-600/80 uppercase tracking-widest mb-1">Custo Total Acumulado</p>
                                                    <p className="text-xl font-black text-amber-700 dark:text-amber-300 tabular-nums">{totalCusto.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</p>
                                                    <p className="text-[10px] text-amber-600 font-medium mt-0.5">{analystDetail.consultas.length} consultas no total</p>
                                                </div>
                                            </div>
                                            {sortedMonths.map(([mesKey, mesData], mi) => {
                                                const [ano, mesNum] = mesKey.split('-');
                                                const mesLabel = `${mesesNomes[parseInt(mesNum) - 1]}/${ano}`;
                                                const isCurrentMonth = mi === 0;
                                                return (
                                                    <div key={mesKey} className="border border-slate-200 dark:border-slate-700/50 rounded-xl overflow-hidden">
                                                        <div className={`px-4 py-2.5 border-b border-slate-100 dark:border-slate-700/50 flex items-center justify-between ${isCurrentMonth ? 'bg-primary/5 dark:bg-primary/10' : 'bg-slate-50 dark:bg-slate-800/60'}`}>
                                                            <div className="flex items-center gap-2">
                                                                <Calendar size={13} className={isCurrentMonth ? 'text-primary' : 'text-slate-400'} />
                                                                <span className={`text-[10px] font-extrabold uppercase tracking-[0.1em] ${isCurrentMonth ? 'text-primary' : 'text-slate-500'}`}>{mesLabel}</span>
                                                                {isCurrentMonth && <span className="px-1.5 py-0.5 text-[8px] font-bold bg-primary/10 text-primary rounded">Atual</span>}
                                                            </div>
                                                            <div className="flex items-center gap-3 text-[10px] font-bold">
                                                                <span className="text-blue-500">{mesData.pf} PF</span>
                                                                <span className="text-emerald-500">{mesData.pj} PJ</span>
                                                                <span className="text-amber-600">{mesData.custo.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</span>
                                                            </div>
                                                        </div>
                                                        <div className="divide-y divide-slate-50 dark:divide-slate-800">
                                                            {mesData.consultas.map((c) => (
                                                                <div key={c.id} className="flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors">
                                                                    <div className="flex-1 min-w-0">
                                                                        <p className="text-xs font-bold text-slate-700 dark:text-slate-200 truncate">{c.cliente_nome}</p>
                                                                        <div className="flex items-center gap-2 mt-0.5">
                                                                            <span className={`px-1.5 py-0.5 text-[9px] font-bold rounded ${c.tipo_pessoa === 'PF' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'}`}>{c.tipo_pessoa}</span>
                                                                            <span className="text-[10px] text-slate-400 tabular-nums">
                                                                                {new Date(c.created_at).toLocaleDateString('pt-BR')} às {new Date(c.created_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                                                                            </span>
                                                                        </div>
                                                                    </div>
                                                                    <div className="flex items-center gap-3 flex-shrink-0">
                                                                        <span className="text-xs font-bold text-amber-600 dark:text-amber-400 tabular-nums">
                                                                            {c.custo.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                                                                        </span>
                                                                        {c.negociacao_id && (
                                                                            <button
                                                                                onClick={() => {
                                                                                    setShowDashboardModal(false);
                                                                                    setAnalystDetail(null);
                                                                                    setSelectedDetailsId(c.negociacao_id!);
                                                                                }}
                                                                                className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] font-bold text-primary bg-primary/5 hover:bg-primary/10 border border-primary/20 transition-all"
                                                                                title="Ver negociação"
                                                                            >
                                                                                <ExternalLink size={10} />
                                                                                Neg. #{c.negociacao_id}
                                                                            </button>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                        <div className="px-4 py-2 bg-slate-50/60 dark:bg-slate-800/30 border-t border-slate-100 dark:border-slate-700/50 flex items-center justify-between">
                                                            <span className="text-[9px] font-extrabold text-slate-400 uppercase tracking-wider">Subtotal {mesLabel}</span>
                                                            <div className="flex items-center gap-3 text-[10px] font-black">
                                                                <span className="text-blue-600">{mesData.pf} PF</span>
                                                                <span className="text-emerald-600">{mesData.pj} PJ</span>
                                                                <span className="text-amber-700">{mesData.custo.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                            <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-primary/5 to-amber-50 dark:from-primary/10 dark:to-amber-900/10 border border-primary/20 dark:border-primary/30 rounded-xl">
                                                <span className="text-[10px] font-extrabold text-slate-600 dark:text-slate-300 uppercase tracking-wider">Total Acumulado (todos os meses)</span>
                                                <div className="flex items-center gap-4 text-xs font-black">
                                                    <span className="text-blue-700 dark:text-blue-300">{totalPF} PF</span>
                                                    <span className="text-emerald-700 dark:text-emerald-300">{totalPJ} PJ</span>
                                                    <span className="text-amber-700 dark:text-amber-300">{totalCusto.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</span>
                                                </div>
                                            </div>
                                        </>
                                    );
                                })()
                            ) : dashboardData ? (
                                <>
                                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                                        <div className="bg-gradient-to-br from-blue-50 to-blue-100/50 dark:from-blue-900/20 dark:to-blue-800/10 rounded-xl p-4 border border-blue-100 dark:border-blue-800/40">
                                            <div className="flex items-center gap-2 mb-2"><Hash size={13} className="text-blue-500" /><span className="text-[9px] font-extrabold text-blue-500/80 uppercase tracking-widest">Consultas PF</span></div>
                                            <p className="text-2xl font-black text-blue-700 dark:text-blue-300 tabular-nums">{dashboardData.totalPF}</p>
                                        </div>
                                        <div className="bg-gradient-to-br from-emerald-50 to-emerald-100/50 dark:from-emerald-900/20 dark:to-emerald-800/10 rounded-xl p-4 border border-emerald-100 dark:border-emerald-800/40">
                                            <div className="flex items-center gap-2 mb-2"><Hash size={13} className="text-emerald-500" /><span className="text-[9px] font-extrabold text-emerald-500/80 uppercase tracking-widest">Consultas PJ</span></div>
                                            <p className="text-2xl font-black text-emerald-700 dark:text-emerald-300 tabular-nums">{dashboardData.totalPJ}</p>
                                        </div>
                                        <div className="bg-gradient-to-br from-slate-50 to-slate-100/50 dark:from-slate-800/40 dark:to-slate-800/20 rounded-xl p-4 border border-slate-200 dark:border-slate-700">
                                            <div className="flex items-center gap-2 mb-2"><TrendingUp size={13} className="text-slate-500" /><span className="text-[9px] font-extrabold text-slate-400 uppercase tracking-widest">Total Geral</span></div>
                                            <p className="text-2xl font-black text-slate-700 dark:text-slate-200 tabular-nums">{dashboardData.totalGeral}</p>
                                        </div>
                                        <div className="bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/10 rounded-xl p-4 border border-amber-200 dark:border-amber-800/40">
                                            <div className="flex items-center gap-2 mb-2"><DollarSign size={13} className="text-amber-600" /><span className="text-[9px] font-extrabold text-amber-600/80 uppercase tracking-widest">Custo Est.</span></div>
                                            <p className="text-2xl font-black text-amber-700 dark:text-amber-300 tabular-nums">{dashboardData.custoEstimado.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</p>
                                        </div>
                                    </div>

                                    <div className="border border-slate-200 dark:border-slate-700/50 rounded-xl overflow-hidden">
                                        <div className="px-4 py-2.5 bg-slate-50 dark:bg-slate-800/60 border-b border-slate-100 dark:border-slate-700/50 flex items-center gap-2">
                                            <Calendar size={13} className="text-slate-400" />
                                            <span className="text-[10px] font-extrabold text-slate-500 uppercase tracking-[0.1em]">Consumo por Mês</span>
                                        </div>
                                        {dashboardData.porMes.length === 0 ? (
                                            <div className="flex flex-col items-center py-6 gap-1.5">
                                                <Database size={18} className="text-slate-300 dark:text-slate-600" />
                                                <p className="text-xs text-slate-400 font-medium">Nenhuma consulta registrada</p>
                                            </div>
                                        ) : (
                                            <div className="overflow-x-auto">
                                                <table className="w-full text-xs">
                                                    <thead>
                                                        <tr className="border-b border-slate-100 dark:border-slate-700/50">
                                                            <th className="text-left px-4 py-2.5 font-extrabold text-[10px] text-slate-400 uppercase tracking-wider">Período</th>
                                                            <th className="text-center px-3 py-2.5 font-extrabold text-[10px] text-blue-500 uppercase tracking-wider">PF</th>
                                                            <th className="text-center px-3 py-2.5 font-extrabold text-[10px] text-emerald-500 uppercase tracking-wider">PJ</th>
                                                            <th className="text-center px-3 py-2.5 font-extrabold text-[10px] text-slate-400 uppercase tracking-wider">Total</th>
                                                            <th className="text-right px-4 py-2.5 font-extrabold text-[10px] text-amber-600 uppercase tracking-wider">Custo Est.</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="divide-y divide-slate-50 dark:divide-slate-800">
                                                        {dashboardData.porMes.map((m, i) => {
                                                            const [ano, mes] = m.mes.split('-');
                                                            const meses = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
                                                            const mesLabel = `${meses[parseInt(mes) - 1]}/${ano}`;
                                                            const maxTotal = Math.max(...dashboardData.porMes.map(x => x.total), 1);
                                                            return (
                                                                <tr key={m.mes} className={`hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors ${i === 0 ? 'bg-amber-50/30 dark:bg-amber-900/5' : ''}`}>
                                                                    <td className="px-4 py-3 font-bold text-slate-700 dark:text-slate-300">
                                                                        <div className="flex items-center gap-2">
                                                                            <span>{mesLabel}</span>
                                                                            {i === 0 && <span className="px-1.5 py-0.5 text-[8px] font-bold bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 rounded">Atual</span>}
                                                                            {m.excedente && <span className="px-1.5 py-0.5 text-[8px] font-bold bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded" title={`Teto de ${TETO_MENSAL.toLocaleString('pt-BR')} consultas excedido — taxa R$ ${CUSTO_PF_EXCEDENTE.toFixed(2)}/PF e R$ ${CUSTO_PJ_EXCEDENTE.toFixed(2)}/PJ aplicada no excedente`}>⚠️ Excedente</span>}
                                                                        </div>
                                                                        <div className="mt-1.5 h-1 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                                                                            <div className={`h-full rounded-full transition-all duration-500 ${m.excedente ? 'bg-gradient-to-r from-orange-400 to-red-400' : 'bg-gradient-to-r from-blue-400 to-emerald-400'}`} style={{ width: `${(m.total / maxTotal) * 100}%` }} />
                                                                        </div>
                                                                    </td>
                                                                    <td className="text-center px-3 py-3 font-bold text-blue-600 dark:text-blue-400 tabular-nums">{m.pf}</td>
                                                                    <td className="text-center px-3 py-3 font-bold text-emerald-600 dark:text-emerald-400 tabular-nums">{m.pj}</td>
                                                                    <td className="text-center px-3 py-3 font-black text-slate-700 dark:text-slate-200 tabular-nums">{m.total}</td>
                                                                    <td className="text-right px-4 py-3 font-bold text-amber-600 dark:text-amber-400 tabular-nums">{m.custo.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</td>
                                                                </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                    <tfoot>
                                                        <tr className="border-t-2 border-slate-200 dark:border-slate-700 bg-slate-50/80 dark:bg-slate-800/40">
                                                            <td className="px-4 py-3 font-extrabold text-[10px] text-slate-500 uppercase tracking-wider">Total Acumulado</td>
                                                            <td className="text-center px-3 py-3 font-black text-blue-700 dark:text-blue-300 tabular-nums">{dashboardData.totalPF}</td>
                                                            <td className="text-center px-3 py-3 font-black text-emerald-700 dark:text-emerald-300 tabular-nums">{dashboardData.totalPJ}</td>
                                                            <td className="text-center px-3 py-3 font-black text-slate-800 dark:text-slate-100 tabular-nums">{dashboardData.totalGeral}</td>
                                                            <td className="text-right px-4 py-3 font-black text-amber-700 dark:text-amber-300 tabular-nums">{dashboardData.custoEstimado.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</td>
                                                        </tr>
                                                    </tfoot>
                                                </table>
                                            </div>
                                        )}
                                    </div>

                                    {dashboardData.porUsuario.length > 0 && (
                                        <div className="border border-slate-200 dark:border-slate-700/50 rounded-xl overflow-hidden">
                                            <div className="px-4 py-2.5 bg-slate-50 dark:bg-slate-800/60 border-b border-slate-100 dark:border-slate-700/50 flex items-center justify-between">
                                                <div className="flex items-center gap-2">
                                                    <User size={13} className="text-slate-400" />
                                                    <span className="text-[10px] font-extrabold text-slate-500 uppercase tracking-[0.1em]">Consumo por Analista</span>
                                                </div>
                                                <span className="text-[9px] text-slate-400 font-medium italic">Clique no nome para ver detalhes</span>
                                            </div>
                                            <div className="divide-y divide-slate-50 dark:divide-slate-800">
                                                {dashboardData.porUsuario.map((u) => (
                                                    <div key={u.id} className="flex items-center justify-between px-4 py-3 hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors">
                                                        <button
                                                            onClick={() => setAnalystDetail({ nome: u.nome, consultas: u.consultas })}
                                                            className="flex items-center gap-2 text-xs font-bold text-primary hover:text-primary/80 transition-all text-left group"
                                                        >
                                                            <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                                                                <User size={13} className="text-primary" />
                                                            </div>
                                                            <span className="group-hover:underline underline-offset-2">{u.nome}</span>
                                                            <ChevronRightIcon size={12} className="text-slate-300 group-hover:text-primary transition-colors" />
                                                        </button>
                                                        <div className="flex items-center gap-3">
                                                            <span className="text-[10px] font-bold text-blue-500 tabular-nums">{u.pf} PF</span>
                                                            <span className="text-[10px] font-bold text-emerald-500 tabular-nums">{u.pj} PJ</span>
                                                            <span className="px-2 py-0.5 text-[10px] font-black bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 rounded-full tabular-nums">{u.total}</span>
                                                            <span className="text-[10px] font-bold text-amber-600 dark:text-amber-400 tabular-nums min-w-[64px] text-right">{u.custo.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</span>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    <div className="flex items-start gap-2.5 bg-amber-50/80 dark:bg-amber-900/10 border border-amber-200/60 dark:border-amber-800/30 rounded-xl p-3">
                                        <AlertTriangle size={14} className="text-amber-500 mt-0.5 flex-shrink-0" />
                                        <div className="space-y-1">
                                            <p className="text-[11px] text-amber-700 dark:text-amber-400 font-semibold">
                                                Precificação em dois tiers — contrato Serasa Experian:
                                            </p>
                                            <p className="text-[11px] text-amber-700 dark:text-amber-400 font-medium leading-relaxed">
                                                ✅ Até <strong>{TETO_MENSAL.toLocaleString('pt-BR')} consultas/mês</strong>: <strong>R$ {CUSTO_PF_CONTRATUAL.toFixed(2)} PF</strong> / <strong>R$ {CUSTO_PJ_CONTRATUAL.toFixed(2)} PJ</strong>
                                            </p>
                                            <p className="text-[11px] text-amber-700 dark:text-amber-400 font-medium leading-relaxed">
                                                ⚠️ Acima de <strong>{TETO_MENSAL.toLocaleString('pt-BR')} consultas/mês</strong>: <strong>R$ {CUSTO_PF_EXCEDENTE.toFixed(2)} PF</strong> / <strong>R$ {CUSTO_PJ_EXCEDENTE.toFixed(2)} PJ</strong> (excedente)
                                            </p>
                                        </div>
                                    </div>
                                </>
                            ) : null}
                        </div>

                        <div className="px-6 py-3.5 border-t border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/20 flex items-center justify-between">
                            {analystDetail ? (
                                <button onClick={() => setAnalystDetail(null)} className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold text-slate-600 dark:text-slate-400 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700 transition-all">
                                    <ArrowLeft size={14} /> Voltar
                                </button>
                            ) : <div />}
                            <button onClick={() => { setShowDashboardModal(false); setAnalystDetail(null); }} className="px-5 py-2.5 rounded-xl text-sm font-bold text-slate-600 dark:text-slate-400 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700 transition-all">
                                Fechar
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

export default GestaoCard;
